import React, { useEffect, useState, useCallback, useMemo, Suspense, lazy } from 'react';
import { KnowledgeItem } from '../../types/learning';
import { generateDailySession } from '../../services/learning/dailyTrainingService';
import { getAllKnowledgeItems, saveKnowledgeItem } from '../../services/storage/knowledgeItemService';
import { updateKnowledgeItemReview } from '../../services/learning/spacedRepetitionService';
import { SuperMemoGrade } from '../../types/learning';
import { MoveSequence } from '../../types/chess';
import { getSequenceMission } from '../../data/sequenceMissions';
import styles from './DailyTrainingPage.module.css';
import { mapSequenceScoreToSuperMemoGrade } from '../../utils/learningGrading';
// Import UI state components
import { LoadingState, EmptyState, CompletedState, ChallengeSkeleton } from './components';
import MissionDebugProvider from 'components/debug/MissionDebugProvider';
import MissionDebugWrapper from 'components/debug/MissionDebugWrapper';

// Import challenge components with React.lazy for code splitting
const MoveChallenge = lazy(() => import('../../components/chess/MoveChallenge'));
const CoordinateChallenge = lazy(() => import('../../components/chess/CoordinateChallenge'));
// Import the DualNBackChallenge using a dynamic import that works with React.lazy
const DualNBackChallenge = lazy(() => 
  import('../../components/learning/DualNBackChallenge/DualNBackChallenge')
);
// Import SequenceChallenge for sequence-type knowledge items
const SequenceChallenge = lazy(() => import('../../components/chess/SequenceChallenge'));
// import MoveChallenge from '../../components/chess/MoveChallenge';
// import CoordinateChallenge from '../../components/chess/CoordinateChallenge';
// import DualNBackChallenge from '../../components/learning/DualNBackChallenge';

/**
 * Helper to extract sequence challenge props from a KnowledgeItem
 * For sequence-type items, the content should contain tactical theme and sequence data
 */
const extractSequenceChallengeProps = (
  item: KnowledgeItem
): { sequence: MoveSequence; difficulty: number; showHints: boolean; unresolvedReason?: string } | null => {
  if (item.type !== 'sequence') return null;

  const [legacyThemePart, legacyMissionPart] = item.content.split(':').map((s) => s.trim());
  const legacyMissionId = /^sequence-\d+$/i.test(legacyMissionPart || '')
    ? legacyMissionPart
    : /^sequence-\d+$/i.test(item.content.trim())
      ? item.content.trim()
      : undefined;

  const metadataSequenceReady = Array.isArray(item.sequenceMoves)
    && item.sequenceMoves.length > 0
    && typeof item.sequenceStartingPosition === 'string'
    && item.sequenceStartingPosition.length > 0;

  let sequence: MoveSequence | null = null;
  if (metadataSequenceReady) {
    sequence = {
      id: item.sequenceMissionId || item.id,
      moves: item.sequenceMoves as string[],
      startingPosition: item.sequenceStartingPosition as string,
      tacticalTheme: item.sequenceTacticalTheme || legacyThemePart || 'general',
      difficulty: Math.max(1, Math.min(5, Math.floor(item.efactor))),
      description: `Practice ${(item.sequenceTacticalTheme || legacyThemePart || 'tactical')} sequence`,
    };
  }

  const missionIdCandidate = item.sequenceMissionId || legacyMissionId;
  if (!sequence && missionIdCandidate) {
    const mission = getSequenceMission(missionIdCandidate);
    if (mission) {
      sequence = {
        id: mission.id,
        moves: mission.sequence,
        startingPosition: mission.position,
        tacticalTheme: mission.tacticalTheme,
        difficulty: mission.difficulty,
        description: mission.description,
      };
    }
  }

  // Show hints for items with low efactor (struggling items)
  const showHints = item.efactor < 2.0 || item.repetition === 0;

  if (!sequence) {
    return {
      sequence: {
        id: item.id,
        moves: [],
        startingPosition: '',
        tacticalTheme: legacyThemePart || 'general',
        difficulty: Math.max(1, Math.min(5, Math.floor(item.efactor))),
        description: 'Sequence data unavailable',
      },
      difficulty: Math.max(1, Math.min(5, Math.floor(item.efactor))),
      showHints,
      unresolvedReason: 'Unable to resolve sequence mission data for this intel packet.',
    };
  }

  return {
    sequence,
    difficulty: sequence.difficulty,
    showHints
  };
};

/**
 * Helper to extract position and targetMove from item.content for move-type items
 */
const extractMoveChallengeProps = (item: KnowledgeItem): { position: string; targetMove: string } => {
  if (item.type === 'move') {
    // First check for the new custom properties
    if ((item as any).fen && (item as any).targetMove) {
      return { 
        position: validateAndFixFEN((item as any).fen), 
        targetMove: (item as any).targetMove 
      };
    }
    
    // Fall back to the old format if the custom properties don't exist
    if (typeof item.content === 'string') {
      const [position, targetMove] = item.content.split('|');
      // Validate and fix the FEN string, particularly the en-passant square
      return { position: validateAndFixFEN(position || ''), targetMove: targetMove || '' };
    }
  }
  return { position: '', targetMove: '' };
};

/**
 * Helper to validate and fix a FEN string, focusing on en-passant squares
 * @param fen FEN string to validate
 * @returns Fixed FEN string
 */
const validateAndFixFEN = (fen: string): string => {
  if (!fen) return ''; 
  
  try {
    const parts = fen.split(' ');
    if (parts.length !== 6) return fen; // Not a standard FEN format
    
    // Check and fix the en-passant square (4th component)
    const enPassantIndex = 3;
    const enPassant = parts[enPassantIndex];
    
    // If it's not "-" and not a valid square (a-h)(1-8), replace with "-"
    if (enPassant !== '-' && !/^[a-h][1-8]$/.test(enPassant)) {
      // Invalid en-passant square, replace with "-"
      parts[enPassantIndex] = '-';
      return parts.join(' ');
    }
    
    return fen;
  } catch (error) {
    console.error('Error validating FEN:', error);
    return fen;
  }
};

/**
 * DailyTrainingPage
 *
 * Displays the user's daily spaced repetition session.
 * Fetches due KnowledgeItems, presents challenges, and updates review state.
 * Optimized with memoization, code splitting, and performance best practices.
 */
const DailyTrainingPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<KnowledgeItem[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Fetch and prepare session on mount
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    
    // Remove setTimeout and use async/await properly
    const fetchSession = async () => {
      try {
        if (!isMounted) return;
        const items = getAllKnowledgeItems();
        // Generate session with items due for review
        const due = generateDailySession(items);
        setSession(due);
        setCurrentIdx(0);
        setCompleted(false);
      } catch (error) {
        console.error("Error loading session:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchSession();
    return () => { isMounted = false; };
  }, []);

  // Handle challenge completion - memoized to prevent unnecessary re-renders
  const handleComplete = useCallback((grade: number) => {
    setSession(prevSession => {
      const item = prevSession[currentIdx];
      if (!item) return prevSession;
      
      // Update item using spaced repetition service
      const updated = updateKnowledgeItemReview(item, grade as SuperMemoGrade);
      saveKnowledgeItem(updated);
      
      // Create a new session array with the updated item
      const newSession = [...prevSession];
      newSession[currentIdx] = updated;
      return newSession;
    });
    
    // Move to next item or finish
    if (currentIdx + 1 < session.length) {
      setCurrentIdx(prev => prev + 1);
    } else {
      setCompleted(true);
    }
  }, [currentIdx, session.length]);

  // Memoize the current item to prevent unnecessary recalculations
  const currentItem = useMemo(() => 
    session[currentIdx],
    [session, currentIdx]
  );

  // Memoize move challenge props extraction
  const moveChallengeProps = useMemo(() => 
    currentItem?.type === 'move' ? extractMoveChallengeProps(currentItem) : null,
    [currentItem]
  );

  // Memoize sequence challenge props extraction
  const sequenceChallengeProps = useMemo(() => 
    currentItem?.type === 'sequence' ? extractSequenceChallengeProps(currentItem) : null,
    [currentItem]
  );

  // Handle sequence challenge completion - convert SequenceChallengeResult to grade
  const handleSequenceComplete = useCallback((result: import('../../components/chess/SequenceChallenge').SequenceChallengeResult) => {
    const grade = result.success ? mapSequenceScoreToSuperMemoGrade(result.score) : 0;
    handleComplete(grade);
  }, [handleComplete]);

  // Render challenge component based on item type - memoized to prevent unnecessary re-renders
  const renderChallenge = useCallback((item: KnowledgeItem) => {
    if (!item) return null;
    
    // Use Suspense for loading states with custom skeleton UI
    return (
      <MissionDebugProvider>
          <MissionDebugWrapper>
            <Suspense fallback={<ChallengeSkeleton />}>

            {item.type === 'move' && moveChallengeProps ? (
              <MoveChallenge
                missionId={item.id}
                item={item}
                onComplete={handleComplete}
                missionTitle={item.content}
                position={moveChallengeProps.position}
                targetMove={moveChallengeProps.targetMove}
              />
            ) : item.type === 'coordinate' ? (
              <CoordinateChallenge item={item} onComplete={handleComplete} />
            ) : item.type === 'memory' ? (
              <DualNBackChallenge item={item} onComplete={handleComplete} />
            ) : item.type === 'sequence' && sequenceChallengeProps ? (
              sequenceChallengeProps.unresolvedReason ? (
                <div data-testid="sequence-resolution-error">
                  <p>{sequenceChallengeProps.unresolvedReason}</p>
                  <button
                    type="button"
                    data-testid="skip-sequence-item"
                    onClick={() => handleComplete(0)}
                  >
                    Skip Intel Packet
                  </button>
                </div>
              ) : (
                <SequenceChallenge
                  sequence={sequenceChallengeProps.sequence}
                  difficulty={sequenceChallengeProps.difficulty}
                  showHints={sequenceChallengeProps.showHints}
                  onComplete={handleSequenceComplete}
                />
              )
            ) : (
              <div>Unknown challenge type: {item.type}</div>
            )}

            </Suspense>
          </MissionDebugWrapper>
      </MissionDebugProvider>
    );
  }, [handleComplete, handleSequenceComplete, moveChallengeProps, sequenceChallengeProps]);

  // Early returns for different states
  if (loading) return <div className={styles.loadingState}><LoadingState /></div>;
  if (session.length === 0) return <div className={styles.emptyState}><EmptyState /></div>;
  if (completed) return <div className={styles.completedState}><CompletedState count={session.length} /></div>;

  return (
    <div className={styles.container}>
      <h2 className={styles.pageTitle}>Daily Briefing</h2>
      <div className={styles.progress}>
        Intel Packet {currentIdx + 1} / {session.length}
      </div>
      <div className={styles.challengeWrapper}>
        {currentItem && renderChallenge(currentItem)}
      </div>
    </div>
  );
};

export default React.memo(DailyTrainingPage); 
